import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Icon,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  SimpleGrid,
  Spinner,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { IoClose } from '@react-icons/all-files/io5/IoClose';
import {
  FilterInputGrouped,
  LoadFarmSearchResultCountQuery,
  LoadFarmSearchResultCountQueryVariables,
  NodeSearchPage,
  SearchPageFragment,
  TermCategoryDetailFragment,
  TermCategoryFragment,
} from '../../generated/types';
import { filtersToQuery } from '../../utils/filters';
import { addParamsToPath } from '../../utils/urls';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/router';
import { useLazyQuery } from '@apollo/client';
import { QueryLoadFarmSearchResultCount } from '../../graphql/LoadFarmSearchResultCount';
import getFilterGroups from '../../utils/getFilterGroups';

interface FilterFormProps {
  initialFilter: number[];
  data: SearchPageFragment;
  onSubmitted?: () => void;
}

interface SearchPageFormValues {
  filters: number[];
}

export const FilterForm: FC<FilterFormProps> = ({
  data,
  initialFilter,
  onSubmitted,
}) => {
  const {
    register,
    setValue,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SearchPageFormValues>({
    defaultValues: {
      filters: initialFilter,
    },
  });

  const router = useRouter();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const [filterResultCount, setFilterResultCount] = useState<number | null>(
    null
  );
  const [loadResultCount, { loading }] = useLazyQuery<
    LoadFarmSearchResultCountQuery,
    LoadFarmSearchResultCountQueryVariables
  >(QueryLoadFarmSearchResultCount);

  const filters = watch('filters');

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    if (filters.length) {
      setTimeout(async () => {
        const variables: LoadFarmSearchResultCountQueryVariables = {
          nodeId: data.id,
          page: router.query?.page
            ? parseInt(router.query.page as string, 10)
            : 1,
          searchQuery: '',
          filter: {
            groups: getFilterGroups(data.searchMoreFilter, filters),
          },
        };

        const result = await loadResultCount({
          variables,
        });

        setFilterResultCount(result?.data?.farmSearch?.total || 0);
      }, 500);
    } else {
      setFilterResultCount(null);
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [filters]);

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);

    if (event.target.checked) {
      setValue('filters', [...filters, value]);
    } else {
      setValue(
        'filters',
        filters.filter((id) => id !== value)
      );
    }
  };

  const onSubmit = (values: SearchPageFormValues) => {
    const filterGroups: FilterInputGrouped[] = getFilterGroups(
      data.searchMoreFilter,
      values.filters
    );
    const filterQuery = filtersToQuery(filterGroups);
    const newPath = addParamsToPath(router.asPath, {
      filters: filterQuery,
      page: null,
    });

    router.push(newPath).then(() => {
      onSubmitted && onSubmitted();
    });
  };

  const flattenedFilters = useMemo(() => {
    const flattened: Record<
      number,
      TermCategoryFragment & TermCategoryDetailFragment
    > = [];

    data.searchMoreFilter.forEach((searchMoreFilterParagraph) => {
      flattened[searchMoreFilterParagraph.category.id] =
        searchMoreFilterParagraph.category;

      if (searchMoreFilterParagraph.subCategories.length) {
        searchMoreFilterParagraph.subCategories.forEach((subCategory) => {
          flattened[subCategory.id] = subCategory;
        });
      }
    });

    return flattened;
  }, [data]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ModalContent borderRadius="sm">
        <ModalHeader>Filter</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {filters?.length > 0 && (
            <Box>
              <Box fontWeight="bold">Ihre Auswahl ({filters.length})</Box>
              <Wrap mt={1}>
                {filters?.map((filterId) => {
                  if (!flattenedFilters[filterId]) return null;

                  return (
                    <WrapItem
                      size="sm"
                      alignItems="center"
                      as={Button}
                      borderRadius="full"
                      border="1px solid"
                      borderColor="primary"
                      bg="primaryLight50"
                      color="primary"
                      px={3}
                      py={1}
                      onClick={() => {
                        setValue(
                          'filters',
                          filters.filter((id) => id !== filterId)
                        );
                      }}
                    >
                      {flattenedFilters[filterId].title}
                      <Icon ml={1} as={IoClose} />
                    </WrapItem>
                  );
                })}
              </Wrap>

              <Divider borderColor="gray.300" opacity={1} my={6} />
            </Box>
          )}

          <VStack
            alignItems="flex-start"
            spacing={6}
            divider={<Divider borderColor="gray.300" opacity={1} />}
          >
            {data.searchMoreFilter.map((searchMoreFilterParagraph) => {
              return (
                <Box width="100%" key={searchMoreFilterParagraph.id}>
                  <Box fontWeight="bold" mb={4}>
                    {searchMoreFilterParagraph.category.title}
                  </Box>

                  {searchMoreFilterParagraph.subCategories.length > 0 ? (
                    <SimpleGrid width="100%" gap={2} columns={2}>
                      {searchMoreFilterParagraph.subCategories.map(
                        (subCategory) => {
                          return (
                            <Checkbox
                              key={subCategory.id}
                              value={subCategory.id}
                              name="filters"
                              isChecked={filters.includes(subCategory.id)}
                              onChange={onChange}
                            >
                              {subCategory.title}
                            </Checkbox>
                          );
                        }
                      )}
                    </SimpleGrid>
                  ) : (
                    <Checkbox
                      name="filters"
                      value={searchMoreFilterParagraph.category.id}
                      isChecked={filters.includes(
                        searchMoreFilterParagraph.category.id
                      )}
                      onChange={onChange}
                    >
                      {searchMoreFilterParagraph.category.title}
                    </Checkbox>
                  )}
                </Box>
              );
            })}
          </VStack>
        </ModalBody>

        <ModalFooter justifyContent="space-between">
          <Box>
            {!loading &&
              filterResultCount !== null &&
              `${filterResultCount} Ergebnis${
                filterResultCount > 1 || filterResultCount === 0 ? 'se' : ''
              }`}
            {loading && <Spinner color="primary" />}
          </Box>
          <Button variant="roundBlue" type="submit">
            Übernehmen
          </Button>
        </ModalFooter>
      </ModalContent>
    </form>
  );
};
